import type { BootstrapData, CompoundData, EnglishCard, EnglishArticle, LearningPlan, Work } from "../types";
import { trialApi } from "./trialApi";

declare global {
  var __EARTH_ONLINE_BROWSER_TRIAL__: boolean | undefined;
}

function isBrowserTrial() {
  return typeof window !== "undefined" && globalThis.__EARTH_ONLINE_BROWSER_TRIAL__ === true;
}

interface ApiEnvelope<T> {
  data?: T;
  error?: { message?: string };
}

function getToken() {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("eo_token") : null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });
  if (response.status === 401) {
    try {
      localStorage.removeItem("eo_token");
    } catch {
      /* ignore */
    }
    if (!location.pathname.startsWith("/login")) {
      location.href = "/login";
    }
    throw new Error("登录已过期，请重新登录");
  }
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (!response.ok || !payload.data) {
    throw new Error(payload.error?.message ?? "操作未完成，请稍后重试");
  }
  if (init?.method && !["GET", "HEAD"].includes(init.method.toUpperCase())) announceDataChange();
  return payload.data;
}

/** Wake other signed-in tabs without exposing any application data. */
export function announceDataChange() {
  try { localStorage.setItem("eo_data_changed_at", `${Date.now()}-${Math.random()}`); } catch { /* Optional cross-tab refresh. */ }
}

export const api = {
  bootstrap: () =>
    isBrowserTrial() ? trialApi.bootstrap() : request<BootstrapData>("/api/bootstrap"),
  create: (kind: string, data: unknown) =>
    isBrowserTrial()
      ? trialApi.create(kind, data)
      : request<{ id: string }>(`/api/${kind}`, {
          method: "POST",
          body: JSON.stringify(data)
        }),
  update: (entityType: string, id: string, data: unknown) =>
    isBrowserTrial()
      ? trialApi.update(entityType, id, data)
      : request<{ id: string }>(`/api/${entityType}/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data)
        }),
  completeTask: (id: string) =>
    isBrowserTrial()
      ? trialApi.completeTask(id)
      : request<{ taskId: string; eventId: string; xp: number; idempotent: boolean }>(
          `/api/tasks/${id}/complete`,
          { method: "POST", body: "{}" }
        ),
  updateTaskStatus: (id: string, status: string) =>
    isBrowserTrial()
      ? trialApi.updateTaskStatus(id, status)
      : request<{ id: string }>(`/api/tasks/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status })
        }),
  reorderContacts: (orderedIds: string[]) =>
    isBrowserTrial()
      ? trialApi.reorderContacts(orderedIds)
      : request<{ ids: string[] }>("/api/contacts/reorder", {
          method: "POST",
          body: JSON.stringify({ orderedIds })
        }),
  updateCustomerOrderStatus: (id: string, status: string) =>
    isBrowserTrial()
      ? trialApi.updateCustomerOrderStatus(id, status)
      : request<{ id: string }>(`/api/customer-orders/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status })
        }),
  uploadOrderImage: (payload: { mimeType: string; dataBase64: string }) =>
    isBrowserTrial()
      ? trialApi.uploadOrderImage(payload)
      : request<{ url: string }>("/api/uploads/order-image", {
          method: "POST",
          body: JSON.stringify(payload)
        }),
  uploadOrderFile: (payload: { name: string; mimeType: string; dataBase64: string }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持文件上传"))
      : request<{ url: string; name: string }>("/api/uploads/order-file", {
          method: "POST",
          body: JSON.stringify(payload)
        }),
  generateReview: () =>
    isBrowserTrial()
      ? trialApi.generateReview()
      : request<{ id: string }>("/api/reviews/generate", { method: "POST", body: "{}" }),
  aiCoach: async (question: string) => {
    if (isBrowserTrial()) throw new Error("试用模式不支持帝王引擎");
    try {
      return await request<{ answer: string; scopes?: string[] }>("/api/ai/coach", {
          method: "POST",
          body: JSON.stringify({ question }),
          signal: AbortSignal.timeout(40_000)
        });
    } catch (error) {
      if (error instanceof Error && ["TimeoutError", "AbortError"].includes(error.name)) {
        throw new Error("云端推理等待超时，请稍后重试");
      }
      throw error;
    }
  },
  feishuReport: (text: string) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ ok: true }>("/api/feishu/report", {
          method: "POST",
          body: JSON.stringify({ text })
        }),
  syncContactToFeishu: (contactId: string) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ url: string }>("/api/feishu/sync-contact", {
          method: "POST",
          body: JSON.stringify({ contactId })
        }),
  syncOrderToFeishu: (orderId: string) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ url: string }>("/api/feishu/sync-order", {
          method: "POST",
          body: JSON.stringify({ orderId })
        }),
  listLearningPlans: () =>
    isBrowserTrial()
      ? Promise.resolve([])
      : request<LearningPlan[]>("/api/learning/plans"),
  createLearningPlan: (payload: { title: string; skillId?: string; goal?: string; totalDays?: number; dailyMinutes?: number; startDate?: string }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>("/api/learning/plans", { method: "POST", body: JSON.stringify(payload) }),
  updateLearningPlan: (id: string, payload: { title?: string; skillId?: string | null; goal?: string; totalDays?: number; dailyMinutes?: number }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>(`/api/learning/plans/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteLearningPlan: (id: string) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>(`/api/learning/plans/${id}`, { method: "DELETE" }),
  completeLearningDay: (dayId: string, reflection: string) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ xp: number }>(`/api/learning/days/${dayId}/complete`, { method: "POST", body: JSON.stringify({ reflection }) }),
  listEnglishCards: () =>
    isBrowserTrial()
      ? Promise.resolve([])
      : request<EnglishCard[]>("/api/english/cards"),
  addEnglishCard: (payload: { sentenceEn: string; sentenceZh?: string; context?: string; source?: string }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>("/api/english/cards", { method: "POST", body: JSON.stringify(payload) }),
  markCardLearned: (cardId: string, correct: boolean) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ learned: number; reviewAt: string }>(`/api/english/cards/${cardId}/learn`, { method: "POST", body: JSON.stringify({ correct }) }),
  listEnglishArticles: () =>
    isBrowserTrial()
      ? Promise.resolve([])
      : request<EnglishArticle[]>("/api/english/articles"),
  addEnglishArticle: (payload: { titleEn: string; titleZh?: string; contentEn?: string; contentZh?: string; category?: string }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>("/api/english/articles", { method: "POST", body: JSON.stringify(payload) }),
  listWorks: () =>
    isBrowserTrial()
      ? Promise.resolve([])
      : request<Work[]>("/api/works"),
  createWork: (payload: { title: string; kind?: string; platform?: string; url?: string; reflection?: string; status?: string }) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>("/api/works", { method: "POST", body: JSON.stringify(payload) }),
  updateWork: (id: string, payload: Partial<Work>) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持"))
      : request<{ id: string }>(`/api/works/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  getCompoundData: () =>
    isBrowserTrial()
      ? Promise.resolve(null)
      : request<CompoundData>("/api/compound"),
  setDailyActionCompletion: (day: string, completed: boolean) =>
    isBrowserTrial()
      ? Promise.reject(new Error("试用模式不支持行动确认"))
      : request<{ day: string; completed: boolean; eventId: string | null; idempotent: boolean; xp: number }>(
          "/api/compound/daily-actions/" + day,
          { method: completed ? "POST" : "DELETE", body: completed ? "{}" : undefined }
        ),
  publishReview: (id: string) =>
    isBrowserTrial()
      ? trialApi.publishReview(id)
      : request<{ id: string }>(`/api/reviews/${id}/publish`, {
          method: "POST",
          body: "{}"
        }),
  remove: (entityType: string, id: string) =>
    isBrowserTrial()
      ? trialApi.remove(entityType, id)
      : request<{ id: string }>(`/api/entities/${entityType}/${id}`, { method: "DELETE" }),
  restore: (entityType: string, id: string) =>
    isBrowserTrial()
      ? trialApi.restore(entityType, id)
      : request<{ id: string }>(`/api/entities/${entityType}/${id}/restore`, {
          method: "POST",
          body: "{}"
        })
};
